import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  environment,
  MyHttpClientFactory,
  ResponseCasingEnum,
} from '@ups/xplat/core';
import { parseData } from '@ups/xplat/utils';

export interface SignedEnrollmentResult {
  trainingId: string;
  trainingEnrollmentId: string;
  signedOn: string;
  signatureFileId: string;
}

@Injectable({
  providedIn: 'root',
})
export class TrainingAPIService {
  httpFactory = inject(MyHttpClientFactory);
  http: HttpClient;
  httpCamelCase: HttpClient;
  dynamicHttp: HttpClient;
  dynamicHttpCamelCase: HttpClient;

  constructor() {
    const url = environment.urls.dynamicAPI;
    this.http = this.httpFactory.createHttpClient(
      url,
      true,
      ResponseCasingEnum.CamelCase
    );
  }

  getCourses() {
    const url = '/api/training/courses';
    return this.http.get(url);
  }

  getEnrollments(facilityId, courseId, trainingDate) {
    const url =
      '/api/training/enrollments/' +
      facilityId +
      '/' +
      courseId +
      '/' +
      trainingDate;
    return this.http.get(url).toPromise().then(parseData);
  }

  signEnrollment(trainingSign): Promise<SignedEnrollmentResult> {
    const url = 'api/Training/enrollment/sign';
    return this.http
      .put<SignedEnrollmentResult>(url, trainingSign)
      .toPromise()
      .then(parseData);
  }

  enrollOrSign(trainingSign): Promise<SignedEnrollmentResult> {
    const url = 'api/Training/enrollment';
    return this.http
      .put<SignedEnrollmentResult>(url, trainingSign)
      .toPromise()
      .then(parseData);
  }

  deleteSignature(trainingEnrollmentId: string) {
    const url = 'api/Training/enrollment/' + trainingEnrollmentId;
    return this.http.delete(url).toPromise().then(parseData);
  }

  getPdf(trainingId: string) {
    const url = `api/Training/enrollment/${trainingId}/complete`;
    return this.http.post(url, {}).toPromise().then(parseData);
  }

  getEnrollees(
    facilityId,
    jobIds: string[],
    courseId?: string,
    trainingDate?: Date
  ) {
    const url = 'api/training/employees/' + facilityId;
    let params = new HttpParams();

    if (jobIds && jobIds.length > 0) {
      params = params.set('vpJobIds', jobIds.join(','));
    }

    if (courseId) {
      params = params.set('absorbCourseId', courseId);
    }

    if (trainingDate) {
      const trainingDateString = trainingDate.toISOString().slice(0, 10);
      params = params.set('trainingDate', trainingDateString);
    }

    return this.http.get(url, { params }).toPromise().then(parseData);
  }

  trainingComplete(trainingId: string) {
    const url = `api/Training/${trainingId}/complete`;

    const headers = new HttpHeaders({
      // eslint-disable-next-line @typescript-eslint/naming-convention
      'Content-Type': 'application/json', // Adjust if needed
    });

    return this.http.post(url, {}, { headers, responseType: 'blob' });
  }
}
